import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';
import Fund from '@/models/Fund';
import chevron from '@/assets/icons/chevron.svg';
import selectChevron from '@/assets/icons/select-chevron.svg';
import time from '@/assets/icons/time.svg';
import SearchInput from '@/components/ui/SearchInput/SearchInput';
import styles from './fundSelect.module.css';

interface FundOption {
  readonly value: number;
  readonly label: string;
}

interface FundSelectProps {
  funds: Fund[];
  historyFunds: Fund[];
  currentFund: Fund | null;
  handleSelectChange: (fundId: number) => void;
  isLoading: boolean;
  /**
   * 'sidebar' — עמודה ברוחב מלא, כפי שהיה בראש התפריט.
   * 'bar' — כפתור צר בתוך פס היתרה, שנפתח מעל הלוח ונגלל איתו.
   */
  variant?: 'sidebar' | 'bar';
}

const FundSelect = ({
  funds,
  historyFunds,
  currentFund,
  handleSelectChange,
  isLoading,
  variant = 'sidebar',
}: FundSelectProps) => {
  const { t } = useTranslation();
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const selectRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null) as React.RefObject<HTMLInputElement>;
  
  // Refs for virtual scrolling
  const historyContainerRef = useRef<HTMLDivElement>(null);
  const regularContainerRef = useRef<HTMLDivElement>(null);

  const fundOptions: FundOption[] = funds.map((f) => ({
    value: f.id,
    label: f.name,
  }));
  const historyFundOptions: FundOption[] = historyFunds.map((f) => ({
    value: f.id,
    label: f.name,
  }));

  const filteredOptions = fundOptions.filter((f) => `${f.value} ${f.label}`.toLowerCase().includes(searchValue.toLowerCase()));
  const filteredHistoryOptions = historyFundOptions.filter((f) => `${f.value} ${f.label}`.toLowerCase().includes(searchValue.toLowerCase()));

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsSelectOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isSelectOpen && searchInputRef.current) {
      searchInputRef.current?.focus();
    }
  }, [isSelectOpen]);

  // Setup virtualizers
  const historyVirtualizer = useVirtualizer({
    count: filteredHistoryOptions.length,
    getScrollElement: () => historyContainerRef.current,
    estimateSize: () => 40,
    overscan: 5,
  });

  const regularVirtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => regularContainerRef.current,
    estimateSize: () => 40,
    overscan: 5,
  });

  const handleSelectClick = (option: FundOption) => {
    setIsSelectOpen(false);
    handleSelectChange(option.value);
    setSearchValue('');
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // First try to select from history, then from regular options
      const firstOption = filteredHistoryOptions.length > 0 
        ? filteredHistoryOptions[0] 
        : filteredOptions.length > 0 
          ? filteredOptions[0] 
          : null;
          
      if (firstOption) {
        handleSelectClick(firstOption);
      }
    }
  };

  const Option = ({ isHistory, fund }: { isHistory: boolean; fund: FundOption }) => (
    <button
      className={styles.option}
      onClick={() => {
        handleSelectClick(fund);
      }}
    >
      {isHistory && <img src={time} alt="" className="me-2" />}
      <span className="body-md-semibold">{fund.value}</span>
      <div className={styles.divider}></div>
      <span className={`body-sm-regular ${styles.optionName}`} title={fund.label}>
        {fund.label}
      </span>
      <span className="ms-auto">
        <img src={chevron} alt="" className={styles.chevron} />
      </span>
    </button>
  );

  return (
    <div ref={selectRef} className={variant === 'bar' ? styles.root : undefined}>
      <button
        className={`body-md-regular ${styles['select-title']} ${isSelectOpen ? styles.open : ''}`}
        onClick={() => setIsSelectOpen(!isSelectOpen)}
        aria-label={t('viewDataForFund')}
        aria-expanded={isSelectOpen}
      >
        {/* הכפתור ברוחב קבוע, ולכן שם ארוך נחתך בו — ה-title הוא איך קוראים
            אותו בלי לפתוח את התפריט. */}
        <span className={styles['select-value']} title={currentFund?.name}>
          {currentFund ? `${currentFund.id}${currentFund.name ? ` | ${currentFund.name}` : ''}` : t('selectFund')}
        </span>
        <span className="flex-shrink-0">
          <img src={selectChevron} alt="" className={isSelectOpen ? 'rotate-180' : ''} />
        </span>
      </button>
      {isSelectOpen && (
        <div className={styles['select-menu']}>
          <SearchInput 
            value={searchValue} 
            onChange={setSearchValue} 
            placeholder={t('searchFund')} 
            className={styles['search-container']} 
            inputRef={searchInputRef}
            onKeyDown={handleKeyDown}
          />
          <div className={styles['options-container']}>
            {isLoading ? (
              <p className={`${styles.option} body-sm-regular`}>{t('loadingFunds')}...</p>
            ) : filteredOptions.length + filteredHistoryOptions.length === 0 ? (
              <p className={`${styles.option} body-sm-regular`}>{t('noFundsFound')}</p>
            ) : (
              <>
                {filteredHistoryOptions.length > 0 && (
                  <div 
                    ref={historyContainerRef} 
                    className={styles['virtual-container']}
                    style={{ 
                      height: Math.min(filteredHistoryOptions.length * 40, filteredOptions.length > 0 ? 100 : 220),
                    }}
                  >
                    <div
                      className={styles['virtual-content']}
                      style={{
                        height: `${historyVirtualizer.getTotalSize()}px`,
                      }}
                    >
                      {historyVirtualizer.getVirtualItems().map(virtualRow => (
                        <div
                          key={virtualRow.key}
                          ref={historyVirtualizer.measureElement}
                          data-index={virtualRow.index}
                          className={styles['virtual-item']}
                          style={{
                            transform: `translateY(${virtualRow.start}px)`,
                          }}
                        >
                          <Option 
                            fund={filteredHistoryOptions[virtualRow.index]} 
                            isHistory={true} 
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {filteredHistoryOptions.length > 0 && filteredOptions.length > 0 && <div className={styles['history-divider']} />}
                
                {filteredOptions.length > 0 && (
                  <div 
                    ref={regularContainerRef} 
                    className={styles['virtual-container']}
                    style={{ 
                      height: Math.min(filteredOptions.length * 40, filteredHistoryOptions.length > 0 ? 100 : 220),
                    }}
                  >
                    <div
                      className={styles['virtual-content']}
                      style={{
                        height: `${regularVirtualizer.getTotalSize()}px`,
                      }}
                    >
                      {regularVirtualizer.getVirtualItems().map(virtualRow => (
                        <div
                          key={virtualRow.key}
                          ref={regularVirtualizer.measureElement}
                          data-index={virtualRow.index}
                          className={styles['virtual-item']}
                          style={{
                            transform: `translateY(${virtualRow.start}px)`,
                          }}
                        >
                          <Option 
                            fund={filteredOptions[virtualRow.index]} 
                            isHistory={false} 
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FundSelect;
